import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_ORIGIN, EMAIL, PHONE, SOCIAL_LINKS, setJsonLd, removeJsonLd } from '@/lib/site';
import { pages, normalPath, noindexFor, redirectFor } from '@/lib/seoPolicy';
import { setMeta, setCanonical, socialImage, breadcrumb } from '@/lib/seo';

export const SeoManager = () => {
  const { pathname, search } = useLocation();
  useLayoutEffect(() => {
    const path = redirectFor(pathname, search) || normalPath(pathname);
    const entry = pages[path];
    document.title = entry?.[0] || 'Regal Tuning | ECU Tools & Vehicle Tuning';
    setMeta('description', entry?.[1] || 'Explore vehicle tuning information, ECU compatibility and professional tuning tools from Regal Tuning.');
    setMeta('robots', noindexFor(path, search) ? 'noindex, follow' : 'index, follow, max-image-preview:large');
    setCanonical(path);
    setMeta('og:type', path.startsWith('/product/') ? 'product' : 'website', true);
    setMeta('og:site_name', 'Regal Tuning', true);
    setMeta('og:locale', 'en_GB', true);
    setMeta('twitter:card', 'summary_large_image');
    const brand = path.includes('alientech') ? 'alientech' : path.includes('autotuner') ? 'autotuner' : path.includes('magic') || path.includes('flex') ? 'magic-motorsport' : path.includes('dimsport') ? 'dimsport' : null;
    socialImage(brand ? `/assets/brands/${brand}.webp` : '/assets/workshop/dyno-lambo.webp');
    setJsonLd('ld-site', { '@context': 'https://schema.org', '@graph': [
      { '@type': 'Organization', '@id': SITE_ORIGIN + '/#organization', name: 'Regal Tuning', url: SITE_ORIGIN + '/',
        logo: SITE_ORIGIN + '/assets/logo/emblem.png', email: EMAIL, telephone: PHONE, sameAs: Object.values(SOCIAL_LINKS) },
      { '@type': 'WebSite', '@id': SITE_ORIGIN + '/#website', name: 'Regal Tuning', url: SITE_ORIGIN + '/',
        publisher: { '@id': SITE_ORIGIN + '/#organization' } },
    ] });
    if (entry && path !== '/' && !path.startsWith('/tuning-tools/')) {
      breadcrumb('ld-page-breadcrumb', [['Home', '/'], [entry[0].split('|')[0].trim(), path]]);
    } else removeJsonLd('ld-page-breadcrumb');
    // Dynamic pages already set accurate titles/descriptions after loading.
    // Mirror those values to social tags, without overriding their content.
    const sync = () => {
      const description = document.querySelector('meta[name="description"]')?.content || '';
      setMeta('og:title', document.title, true); setMeta('twitter:title', document.title);
      setMeta('og:description', description, true); setMeta('twitter:description', description);
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) setMeta('og:url', canonical.href, true);
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.head, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['content', 'href'] });
    return () => observer.disconnect();
  }, [pathname, search]);
  return null;
};